import { cachedBitmap, loadCraterMask, sharedArt } from './art-cache';
import type { AssetCatalog } from './assets';
import { gameLayerPaths, type PlayerLook } from './character-sheet';
import { CharacterSkin } from './character-skin';
import { UiAtlas } from './ui-atlas';
import { ballsOfWeapon } from './weapon-balls';

export interface PreloadPlayer {
  accountId: string;
  /** Verdadeiro = masculino. */
  sex: boolean;
  weaponTemplateId: number;
  /** Bombas cujo desenho o estágio visual da arma troca (ADR 0032); só a arte delas baixa. */
  visualBallIds?: readonly number[];
  /** Aparência já montada pelo catálogo (`lookOf`); sem ela vale o visual padrão, como no renderer. */
  look?: PlayerLook;
}

export interface MatchPreload {
  assets: AssetCatalog;
  mapId: number;
  players: readonly PreloadPlayer[];
  craterMaskBase?: string;
}

/**
 * Baixa e decodifica, antes de a partida abrir, o que o renderer vai pedir logo no primeiro quadro: o cenário (fundo e terreno), as folhas e as asas
 * de cada jogador, a arte, a cratera e a borda das bombas das armas, o rastro das partículas e a cova. Usa as mesmas bibliotecas do renderer
 * (`sharedArt`), então a partida as encontra prontas. Nunca rejeita: o que falhar aqui o renderer tenta de novo na hora.
 */
export async function preloadMatch({ assets, mapId, players, craterMaskBase }: MatchPreload): Promise<void> {
  const art = sharedArt(assets);
  const tasks: Promise<unknown>[] = [];

  const map = assets.mapArt(mapId);
  if (map.back) tasks.push(cachedBitmap(assets.url(map.back)));
  if (map.fore) tasks.push(cachedBitmap(assets.url(map.fore)));

  const balls = new Set<number>();
  const visualOnly = new Set<number>();
  for (const p of players) {
    const look: PlayerLook = p.look ?? { sex: p.sex ? 'm' : 'f', worn: [], weaponPic: '' };
    tasks.push(CharacterSkin.load(assets, p.sex ? 'm' : 'f', gameLayerPaths(look, (path) => assets.has(path))));
    if (look.wingPic) tasks.push(art.wingArt.load(look.wingPic));
    for (const ballId of ballsOfWeapon(p.weaponTemplateId)) balls.add(ballId);
    for (const ballId of p.visualBallIds ?? []) visualOnly.add(ballId);
  }
  for (const ballId of visualOnly) if (!balls.has(ballId)) tasks.push(art.bombArt.load(ballId));
  for (const ballId of balls) {
    tasks.push(art.bombArt.load(ballId), loadCraterMask(assets, ballId, craterMaskBase));
  }
  if (balls.size > 0) tasks.push(art.brinks.load());
  tasks.push(art.particleArt.preload());
  tasks.push(UiAtlas.load(assets, 'game').then((atlas) => atlas?.blob('asset.game.tombAsset')));

  await Promise.allSettled(tasks);
}
